import { AddLessonModal } from "@/components/lessons/AddLessonModal";
import { AddAudioFolderModal } from "@/components/projects/AddAudioFolderModal";
import { AddProjectModal } from "@/components/projects/AddProjectModal";
import { useAuth } from "@/contexts/AuthContext";
import { useSelection } from "@/contexts/SelectionContext";
import { IconButton } from "@ai-teach/ui-web";
import { useEffect, useRef, useState } from "react";
import { HiPlus } from "react-icons/hi";

interface AddItemModalProps {
  className?: string;
  parentId?: string | undefined;
  dropUp?: boolean;
}

/**
 * Dropdown button component for adding new projects or lessons
 * Includes the trigger button, dropdown menu, and modals
 */
export function AddItemModal({ className = "", parentId, dropUp = false }: AddItemModalProps) {
  const [addItemModalOpen, setAddItemModalOpen] = useState<boolean>(false);
  const [addNewProjectModalOpen, setAddNewProjectModalOpen] = useState<boolean>(false);
  const [addNewLessonModalOpen, setAddNewLessonModalOpen] = useState<boolean>(false);
  const [addAudioFolderModalOpen, setAddAudioFolderModalOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleAddItem = () => {
    setAddItemModalOpen((prev) => !prev);
  };

  const { toggleWarningModal } = useSelection();
  const { user } = useAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setAddItemModalOpen(false);
      }
    };

    if (addItemModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [addItemModalOpen]);

  return (
    <div className={`relative ${className}`}>
      <IconButton
        ref={buttonRef}
        aria-label="Add new content"
        variant="primary"
        shape="circle"
        size="md"
        onClick={handleAddItem}
      >
        <HiPlus className="w-5 h-5" aria-hidden="true" title="Add Item" />
      </IconButton>

      {/* Add Item Dropdown */}
      {addItemModalOpen && (
        <div
          ref={dropdownRef}
          className={`panel absolute ${dropUp ? "bottom-full mb-2" : "top-full mt-2"} right-0 w-56 py-3 z-[60] ring-1 ring-gray-700 bg-slate-50`}
        >
          <h3 className="px-5 py-2 h6">Add new</h3>
          <div className="border-t border-gray-700 mt-2">
            <button
              type="button"
              onClick={() => {
                setAddItemModalOpen(false);
                if (user?.authSource !== "mybestuser") {
                  toggleWarningModal("Only myBestUser user can create a new project");
                } else {
                  setAddNewProjectModalOpen(true);
                }
              }}
              className="w-full px-5 py-3 text-left text-secondary-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors label"
            >
              Project
            </button>
            <button
              type="button"
              onClick={() => {
                setAddItemModalOpen(false);
                setAddNewLessonModalOpen(true);
              }}
              className="w-full px-5 py-3 text-left text-secondary-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors label"
            >
              Lesson
            </button>
            <button
              type="button"
              onClick={() => {
                setAddItemModalOpen(false);
                if (user?.authSource !== "mybestuser") {
                  toggleWarningModal("Only myBestUser user can create a new audio folder");
                } else {
                  setAddAudioFolderModalOpen(true);
                }
              }}
              className="w-full px-5 py-3 text-left text-secondary-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors label"
            >
              Audio Folder
            </button>
          </div>
        </div>
      )}

      {addNewProjectModalOpen && (
        <AddProjectModal
          isOpen={addNewProjectModalOpen}
          onClose={() => setAddNewProjectModalOpen(false)}
          parentId={parentId || null}
        />
      )}

      {addNewLessonModalOpen && (
        <AddLessonModal
          isOpen={addNewLessonModalOpen}
          onClose={() => setAddNewLessonModalOpen(false)}
          parentId={parentId || null}
        />
      )}

      {addAudioFolderModalOpen && (
        <AddAudioFolderModal
          isOpen={addAudioFolderModalOpen}
          onClose={() => setAddAudioFolderModalOpen(false)}
          parentId={parentId || null}
        />
      )}
    </div>
  );
}
